import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import SEO, { SITE_NAME } from '../../Components/SEO';
import './flashcards.css';
import { setsMeta, loadSet } from './FlashcardSets';

const Flashcard = ({
  sets: setsProp,
  initialSetId = null,
  onComplete,
}) => {
  const [sets, setSets] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    if (setsProp?.length) {
      setSets(setsProp);
      setLoaded(true);
    } else if (setsProp?.cards?.length) {
      setSets([setsProp]);
      setLoaded(true);
    } else {
      Promise.all(
        setsMeta.map(async (meta) => {
          const data = await loadSet(meta.id);
          return { ...meta, cards: data.cards };
        })
      ).then((data) => {
        setSets(data);
        setLoaded(true);
      }).catch(() => setLoaded(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setsProp]);

  // ---- filter state ----
  const [query, setQuery] = useState('');
  const [topicFilter, setTopicFilter] = useState('all');
  const [cardLevelFilter, setCardLevelFilter] = useState('all');
  const [hasStarted, setHasStarted] = useState(false);

  // Mark as started once any filter deviates from default
  useEffect(() => {
    if (query !== '' || topicFilter !== 'all' || cardLevelFilter !== 'all') {
      setHasStarted(true);
    }
  }, [query, topicFilter, cardLevelFilter]);

  // derived option lists
  const topics = useMemo(
    () => ['all', ...Array.from(new Set(sets.map((s) => s.topic).filter(Boolean)))],
    [sets]
  );
  const levels = useMemo(() => {
    const cardLevels = sets.flatMap((s) =>
      s.cards.map((c) => c.level).filter(Boolean)
    );
    return ['all', ...Array.from(new Set(cardLevels))];
  }, [sets]);

  // ---- filtering ----
  const filteredSets = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sets.filter((set) => {
      if (topicFilter !== 'all' && set.topic !== topicFilter) return false;
      if (!q) return true;
      if (set.title.toLowerCase().includes(q)) return true;
      return set.cards.some((c) =>
        [c.word, c.definition, c.translation, c.example]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(q))
      );
    });
  }, [sets, query, topicFilter]);

  // ---- active set ----
  const [activeSetId, setActiveSetId] = useState(initialSetId);

  useEffect(() => {
    if (!hasStarted) return;
    if (initialSetId) {
      setActiveSetId(initialSetId);
    } else if (filteredSets.length > 0) {
      setActiveSetId((prev) => {
        if (!prev || !filteredSets.some((s) => s.id === prev)) {
          return filteredSets[0].id;
        }
        return prev;
      });
    } else {
      setActiveSetId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSets, initialSetId, hasStarted]);

  const activeSet = useMemo(
    () => filteredSets.find((s) => s.id === activeSetId) || null,
    [filteredSets, activeSetId]
  );

  // ✅ Memoize cards so the array reference is stable per (activeSet, cardLevelFilter)
  const cards = useMemo(() => {
    const allCards = activeSet?.cards ?? [];
    return cardLevelFilter !== 'all'
      ? allCards.filter((c) => c.level === cardLevelFilter)
      : allCards;
  }, [activeSet, cardLevelFilter]);

  const cardCount = cards.length;

  // ---- card state ----
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [results, setResults] = useState([]);
  const [startFromBack, setStartFromBack] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [order, setOrder] = useState([]);

  // Reset when active set changes
  useEffect(() => {
    setCurrentIndex(0);
    setResults([]);
    setIsFlipped(startFromBack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSetId]);

  // ✅ Depend on cardCount (primitive) instead of cards (array ref)
  useEffect(() => {
    const indices = Array.from({ length: cardCount }, (_, i) => i);
    if (shuffle) {
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
    }
    setOrder(indices);
    setCurrentIndex(0);
  }, [shuffle, activeSetId, cardCount]);

  const currentCard = order.length ? cards[order[currentIndex]] : undefined;

  useEffect(() => {
    setIsFlipped(startFromBack);
  }, [currentIndex, order, startFromBack]);

  const handleResult = useCallback((result) => {
    if (!currentCard) return;
    const newResults = [...results, { id: currentCard.id, result }];
    setResults(newResults);
    if (currentIndex === cardCount - 1) {
      onComplete?.(newResults);
      return;
    }
    setCurrentIndex((prev) => prev + 1);
  }, [results, currentCard, currentIndex, cardCount, onComplete]);

  const goNext = useCallback(() => {
    if (currentIndex < cardCount - 1) setCurrentIndex((p) => p + 1);
  }, [currentIndex, cardCount]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex((p) => p - 1);
  }, [currentIndex]);

  const toggleFlip = useCallback(() => setIsFlipped((p) => !p), []);

  useEffect(() => {
    if (!activeSet) return;
    const handleKey = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      switch (e.key) {
        case 'ArrowRight': goNext(); break;
        case 'ArrowLeft': goPrev(); break;
        case ' ': e.preventDefault(); toggleFlip(); break;
        case '1': if (isFlipped) handleResult('again'); break;
        case '2': if (isFlipped) handleResult('hard'); break;
        case '3': if (isFlipped) handleResult('easy'); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeSet, currentIndex, isFlipped, results, goNext, goPrev, toggleFlip, handleResult]);

  // ---- render ----
  return (
    <div className="flashcard-activity">
      <SEO
        title="Free English Vocabulary Flashcards"
        description="Free English vocabulary flashcards by topic and level. Flip through cards, mark them again, hard or easy, and track what you have learned."
        name={SITE_NAME}
        type="article"
      />

      {/* Filter bar — always visible above the fold */}
      <div className="flashcard-filters">
        <div className="flashcard-search">
          <span className="flashcard-search-icon" aria-hidden>🔍</span>
          <input
            type="search"
            className="flashcard-search-input"
            placeholder="Search sets, words, definitions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="flashcard-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >×</button>
          )}
        </div>

        <div className="flashcard-filter-row">
          <label className="flashcard-filter">
            <span className="flashcard-filter-label">Topic</span>
            <select
              className="flashcard-filter-select"
              value={topicFilter}
              onChange={(e) => setTopicFilter(e.target.value)}
            >
              {topics.map((t) => (
                <option key={t} value={t}>
                  {t === 'all' ? 'All topics' : t}
                </option>
              ))}
            </select>
          </label>

          <label className="flashcard-filter">
            <span className="flashcard-filter-label">Level</span>
            <select
              className="flashcard-filter-select"
              value={cardLevelFilter}
              onChange={(e) => setCardLevelFilter(e.target.value)}
            >
              {levels.map((l) => (
                <option key={l} value={l}>
                  {l === 'all' ? 'All levels' : l}
                </option>
              ))}
            </select>
          </label>

          {(query || topicFilter !== 'all' || cardLevelFilter !== 'all') && (
            <button
              type="button"
              className="flashcard-filter-clear"
              onClick={() => {
                setQuery('');
                setTopicFilter('all');
                setCardLevelFilter('all');
              }}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* No matching sets */}
      {filteredSets.length === 0 ? (
        <div className="flashcard-empty">
          No sets match your filters.
        </div>
      ) : !activeSetId ? (
        <div className="flashcard-placeholder">
          <div className="flashcard-stage">
            <div className="flashcard flashcard-placeholder-card">
              <div className="flashcard-inner">
                <div className="flashcard-face flashcard-front">
                  <span className="flashcard-label">English</span>
                  <h2 className="flashcard-word">—</h2>
                  <span className="flashcard-hint">Choose a topic to begin</span>
                </div>
                <div className="flashcard-face flashcard-back">
                  <span className="flashcard-label">Meaning</span>
                  <p className="flashcard-definition">Select a topic from the dropdown above</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : !cardCount || !currentCard ? (
        <div className="flashcard-empty">
          This set has no cards.
        </div>
      ) : (
        <>
          {/* Study view — always visible above the fold */}
          <div className="flashcard-study-header">
            <h2 className="flashcard-study-title">{activeSet.title}</h2>
          </div>

          {/* TOP BAR */}
          <div className="flashcard-topbar">
            <div className="flashcard-progress">
              <span className="flashcard-progress-text">{currentIndex + 1} / {cardCount}</span>
              <div className="flashcard-progress-bar">
                <div className="flashcard-progress-fill" style={{ width: `${((currentIndex + 1) / cardCount) * 100}%` }} />
              </div>
            </div>
            <div className="flashcard-stats">
              {['again', 'hard', 'easy'].map((r) => (
                <span key={r} className={`flashcard-stat flashcard-stat-${r}`}>
                  {results.filter((x) => x.result === r).length}
                </span>
              ))}
            </div>
          </div>

          {/* STAGE */}
          <div className="flashcard-stage">
            <button
              type="button"
              className={`flashcard ${isFlipped ? 'is-flipped' : ''}`}
              onClick={toggleFlip}
              aria-label={isFlipped ? 'Show the word' : 'Reveal the answer'}
            >
              <div className="flashcard-inner">
                <div className="flashcard-face flashcard-front">
                  <span className="flashcard-label">English</span>
                  <h2 className="flashcard-word">{currentCard.word}</h2>
                  {currentCard.partOfSpeech && (
                    <span className="flashcard-part-of-speech">{currentCard.partOfSpeech}</span>
                  )}
                  <span className="flashcard-hint">Tap to reveal</span>
                </div>

                <div className="flashcard-face flashcard-back">
                  <span className="flashcard-label">Meaning</span>
                  <p className="flashcard-definition">{currentCard.definition}</p>
                  {currentCard.translation && (
                    <p className="flashcard-translation">{currentCard.translation}</p>
                  )}
                  {currentCard.example && (
                    <p className="flashcard-example">“{currentCard.example}”</p>
                  )}
                </div>
              </div>
            </button>

            <div className="flashcard-nav-row">
              <button type="button" className="flashcard-nav flashcard-nav-prev"
                onClick={goPrev} disabled={currentIndex === 0} aria-label="Previous card">‹</button>
              <button type="button" className="flashcard-nav flashcard-nav-next"
                onClick={goNext} disabled={currentIndex === cardCount - 1}
                aria-label="Next card">›</button>
            </div>
          </div>

          {/* CONTROL BAR */}
          <div className="flashcard-controlbar">
            <div className="flashcard-control-group">
              <button
                type="button"
                className={`flashcard-iconbtn ${startFromBack ? 'is-active' : ''}`}
                onClick={() => setStartFromBack((v) => !v)}
                title="Start from the flipside"
              >
                <span className="flashcard-iconbtn-icon">⇄</span>
                <span className="flashcard-iconbtn-label">Flip side first</span>
              </button>
              <button
                type="button"
                className={`flashcard-iconbtn ${shuffle ? 'is-active' : ''}`}
                onClick={() => setShuffle((v) => !v)}
                title="Shuffle cards"
              >
                <span className="flashcard-iconbtn-icon">🔀</span>
                <span className="flashcard-iconbtn-label">Shuffle</span>
              </button>
            </div>

            <div className="flashcard-control-group flashcard-control-group-right">
              {isFlipped ? (
                <div className="flashcard-buttons">
                  <button type="button" className="flashcard-button flashcard-button-again"
                    onClick={() => handleResult('again')}>Again <kbd>1</kbd></button>
                  <button type="button" className="flashcard-button flashcard-button-hard"
                    onClick={() => handleResult('hard')}>Hard <kbd>2</kbd></button>
                  <button type="button" className="flashcard-button flashcard-button-easy"
                    onClick={() => handleResult('easy')}>Easy <kbd>3</kbd></button>
                </div>
              ) : (
                <span className="flashcard-controlbar-hint">
                  Tap the card or press <kbd>Space</kbd> to reveal
                </span>
              )}
            </div>
          </div>

          <div className="flashcard-shortcuts">
            <span><kbd>←</kbd> <kbd>→</kbd> Navigate</span>
            <span><kbd>Space</kbd> Flip</span>
            <span><kbd>1</kbd><kbd>2</kbd><kbd>3</kbd> Rate</span>
          </div>
        </>
      )}
    </div>
  );
};

export default Flashcard;